import { useState } from "react";

import { useTranslation } from "react-i18next";

import { axisStep, showAxisLabel } from "./axis";
import { useMeasuredWidth } from "./measure";

export interface ColumnDatum {
  /** Tartiblash va `key` uchun (`YYYY-MM-DD`). */
  key: string;
  /** O'qdagi qisqa yorliq. */
  label: string;
  value: number;
  /** Qalqib chiquvchi qatorda qiymatdan keyin keladigan izoh. */
  detail?: string;
}

/** O'lchashdan oldingi (SSR, test) kenglik. */
const FALLBACK_WIDTH = 520;
const HEIGHT = 220;
const PADDING = { top: 12, right: 8, bottom: 28, left: 8 };
const MAX_BAR = 24;
const RADIUS = 4;

/**
 * Ustun uchi yumaloq, asosi to'g'ri — ustun nol chizig'iga "o'tirgan"
 * bo'lib ko'rinadi. Manfiy ustunda yumaloq uch pastda.
 */
export const barPath = (x: number, width: number, base: number, end: number): string => {
  const height = Math.abs(base - end);
  if (height === 0) return "";
  const r = Math.min(RADIUS, height, width / 2);
  if (end < base) {
    return `M${x},${base}V${end + r}Q${x},${end} ${x + r},${end}H${x + width - r}Q${x + width},${end} ${x + width},${end + r}V${base}Z`;
  }
  return `M${x},${base}V${end - r}Q${x},${end} ${x + r},${end}H${x + width - r}Q${x + width},${end} ${x + width},${end - r}V${base}Z`;
};

/**
 * Ustunli grafik — BITTA qator, manfiy qiymat bilan ham.
 *
 * Sof tushum bir bo'lakda manfiy bo'lishi mumkin (qaytarish olingan
 * puldan ko'p): shunda ustun nol chizig'idan PASTGA tushadi. Chiziqli
 * grafik buni "pasayish" deb ko'rsatardi, ustun esa "shu kunda minus"
 * deydi — aynan kerakli ma'no.
 *
 * Bitta rang (`--chart-1`), legenda yo'q — sarlavha nima chizilganini
 * aytadi. Ustun eni ko'pi bilan 24px; qiymat faqat rangga emas, pastdagi
 * matnga va karta jadvaliga ham yoziladi.
 */
export const ColumnChart = ({
  data,
  format,
  ariaLabel,
}: {
  data: ColumnDatum[];
  format: (value: number) => string;
  ariaLabel: string;
}) => {
  const { t } = useTranslation();
  const [active, setActive] = useState<number | null>(null);
  const [frameRef, width] = useMeasuredWidth<HTMLDivElement>(FALLBACK_WIDTH);

  if (data.length === 0) return null;

  const values = data.map(item => item.value);
  const max = Math.max(0, ...values);
  const min = Math.min(0, ...values);
  const span = max - min || 1;

  const plotWidth = width - PADDING.left - PADDING.right;
  const plotHeight = HEIGHT - PADDING.top - PADDING.bottom;
  const slot = plotWidth / data.length;
  const barWidth = Math.max(2, Math.min(MAX_BAR, slot - 4));
  const y = (value: number) => PADDING.top + ((max - value) / span) * plotHeight;
  const baseline = y(0);
  const labelStep = axisStep(data.length);
  const current = active === null ? undefined : data[active];

  return (
    <div ref={frameRef} className="flex flex-col gap-2">
      <svg
        viewBox={`0 0 ${width} ${HEIGHT}`}
        width="100%"
        height={HEIGHT}
        className="block"
        role="img"
        aria-label={ariaLabel}
      >
        {data.map((item, index) => {
          const x = PADDING.left + index * slot + (slot - barWidth) / 2;
          return (
            <path
              key={item.key}
              d={barPath(x, barWidth, baseline, y(item.value))}
              fill="var(--chart-1)"
              opacity={active === null || active === index ? 1 : 0.45}
            />
          );
        })}

        {/* Nol chizig'i ustunlar USTIDA — manfiy ustun qayerdan boshlanishi ko'rinsin. */}
        <line
          x1={PADDING.left}
          x2={width - PADDING.right}
          y1={baseline}
          y2={baseline}
          stroke="var(--border)"
          strokeWidth={1}
        />

        {data.map((item, index) => (
          <rect
            key={`hit-${item.key}`}
            x={PADDING.left + index * slot}
            y={PADDING.top}
            width={slot}
            height={plotHeight}
            fill="transparent"
            tabIndex={0}
            role="button"
            aria-label={`${item.label}: ${format(item.value)}`}
            onMouseEnter={() => setActive(index)}
            onMouseLeave={() => setActive(null)}
            onFocus={() => setActive(index)}
            onBlur={() => setActive(null)}
          />
        ))}

        {data.map((item, index) => (
          <text
            key={`label-${item.key}`}
            opacity={showAxisLabel(index, data.length, labelStep) ? 1 : 0}
            x={PADDING.left + index * slot + slot / 2}
            y={HEIGHT - 8}
            textAnchor="middle"
            className="fill-muted-foreground text-[11px] tabular-nums"
          >
            {item.label}
          </text>
        ))}
      </svg>

      <p className="text-muted-foreground min-h-5 text-sm" aria-live="polite">
        {current
          ? [`${current.label} — ${format(current.value)}`, current.detail]
              .filter(Boolean)
              .join(" · ")
          : t("REPORTS.HOVER_HINT")}
      </p>
    </div>
  );
};
